import { APIProcessName } from "./process-names";

/**
 * Platform API endpoints that write the same audit rows as a History source.
 * Every History source declares this explicitly: a non-empty list, or `null`
 * when the Platform API has no counterpart.
 */
export type PlatformHistoryTwins =
    | readonly [APIProcessName, ...APIProcessName[]]
    | null;

export type PlatformHistoryTwinsBySourceType = Readonly<
    Record<string, PlatformHistoryTwins>
>;

interface PlatformTwinSource {
    sourceType: string;
    platformTwins: PlatformHistoryTwins;
}

/** Platform process names that intentionally never reach the History feed. */
export const PLATFORM_PROCESSES_WITHOUT_HISTORY: readonly string[] = [
    APIProcessName.PLATFORM_GET_ORGANIZATION,
    APIProcessName.PLATFORM_LIST_WORKSPACES,
    APIProcessName.PLATFORM_CREATE_WORKSPACE,
    APIProcessName.PLATFORM_GET_WORKSPACE,
    APIProcessName.PLATFORM_UPDATE_WORKSPACE,
    APIProcessName.PLATFORM_DELETE_WORKSPACE,
    APIProcessName.PLATFORM_LIST_WORKSPACE_ENTITIES,
    APIProcessName.PLATFORM_CREATE_WORKSPACE_ENTITY,
    APIProcessName.PLATFORM_GET_ENTITY,
    APIProcessName.PLATFORM_DELETE_ENTITY,
    APIProcessName.PLATFORM_UPDATE_ENTITY,
    APIProcessName.PLATFORM_CREATE_PLAID_CONNECTION,
    APIProcessName.PLATFORM_LIST_PLAID_CONNECTIONS,
    APIProcessName.PLATFORM_DELETE_PLAID_CONNECTION,
    APIProcessName.PLATFORM_CREATE_LEDGER,
    APIProcessName.PLATFORM_LIST_JOURNAL_ENTRIES,
    APIProcessName.PLATFORM_GET_JOURNAL_ENTRY,
    APIProcessName.PLATFORM_GET_PROFIT_AND_LOSS,
    APIProcessName.PLATFORM_GET_BALANCE_SHEET,
    APIProcessName.PLATFORM_GET_TRIAL_BALANCE,
    APIProcessName.PLATFORM_GET_GENERAL_LEDGER,
    APIProcessName.PLATFORM_GET_CASH_FLOW,
    APIProcessName.PLATFORM_LIST_CHART_OF_ACCOUNTS,
    APIProcessName.PLATFORM_GET_ACCOUNT,
    APIProcessName.PLATFORM_PREPARE_CATEGORIZATION,
    APIProcessName.PLATFORM_LIST_ACCOUNT_GROUPS,
    APIProcessName.PLATFORM_GET_ACCOUNT_GROUP,
    APIProcessName.PLATFORM_LIST_TRANSACTIONS,
    APIProcessName.PLATFORM_GET_TRANSACTION,
    APIProcessName.PLATFORM_LIST_CLASSES,
    APIProcessName.PLATFORM_GET_CLASS,
    APIProcessName.PLATFORM_LIST_TRANSACTION_RULES,
    APIProcessName.PLATFORM_GET_TRANSACTION_RULE,
    APIProcessName.PLATFORM_CREATE_ACCOUNTING_MIGRATION,
    APIProcessName.PLATFORM_GET_ACCOUNTING_MIGRATION,
    APIProcessName.PLATFORM_GENERATE_MIGRATION_TRANSACTION_RULES,
    APIProcessName.PLATFORM_LIST_OPENING_BALANCES,
    APIProcessName.PLATFORM_GET_OPENING_BALANCE,
    APIProcessName.PLATFORM_SET_OPENING_BALANCE,
    APIProcessName.PLATFORM_SET_OPENING_BALANCE_ADJUSTMENT,
    APIProcessName.PLATFORM_DELETE_OPENING_BALANCE_ADJUSTMENT,
    APIProcessName.PLATFORM_BULK_SET_OPENING_BALANCES,
    APIProcessName.PLATFORM_LIST_FINANCIAL_ACCOUNTS,
    APIProcessName.PLATFORM_MOVE_FINANCIAL_ACCOUNT,
    APIProcessName.PLATFORM_LIST_LOCKS,
    APIProcessName.PLATFORM_UPDATE_LOCK,
    APIProcessName.PLATFORM_LIST_USERS,
    APIProcessName.PLATFORM_LIST_TASKS,
    APIProcessName.PLATFORM_GET_TASK,
    APIProcessName.PLATFORM_LIST_TASK_MESSAGES,
    APIProcessName.PLATFORM_CREATE_TASK_MESSAGE,
    APIProcessName.PLATFORM_LIST_TASK_GROUPS,
];

export function toPlatformTwinSources(
    platformTwinsBySourceType: PlatformHistoryTwinsBySourceType,
): PlatformTwinSource[] {
    return Object.entries(platformTwinsBySourceType).map(
        ([sourceType, platformTwins]) => ({ sourceType, platformTwins }),
    );
}

export function expandWithPlatformTwins(
    sources: readonly PlatformTwinSource[],
): string[] {
    return sources.flatMap(({ sourceType, platformTwins }) => [
        sourceType,
        ...(platformTwins ?? []),
    ]);
}

export function withPlatformTwinConfigs<T extends PlatformTwinSource>(
    configs: readonly T[],
): T[] {
    return configs.flatMap((config) => [
        config,
        ...(config.platformTwins ?? []).map((platformTwin) => ({
            ...config,
            sourceType: platformTwin,
            platformTwins: null,
        })),
    ]);
}

export function withPlatformTwinTemplates<T>(
    templates: Readonly<Record<string, T>>,
    sources: readonly PlatformTwinSource[],
): Record<string, T> {
    const twinTemplates = sources.flatMap(
        ({ sourceType, platformTwins }): Array<[string, T]> => {
            const template = templates[sourceType];
            if (template === undefined) {
                return [];
            }
            return (platformTwins ?? []).map((platformTwin) => [
                platformTwin,
                template,
            ]);
        },
    );
    return { ...templates, ...Object.fromEntries(twinTemplates) };
}

export function buildInternalSourceTypeByPlatformTwin(
    sources: readonly PlatformTwinSource[],
): ReadonlyMap<string, string> {
    return new Map(
        sources.flatMap(({ sourceType, platformTwins }) =>
            (platformTwins ?? []).map((platformTwin): [string, string] => [
                platformTwin,
                sourceType,
            ]),
        ),
    );
}
